import operations from '~/helpers/device-operations';
import { alimStatus, firmwareCameras, monitoring, sdStatus, shield, wifiChip } from '~/helpers/fields-description';
import {
    connection,
    connectionTime,
    ip,
    lastDisconnection,
    networkLock, outdated,
    publicIp,
    vpnIp,
    vpnServer,
} from '~/helpers/network-fields-description';
import { FieldObject } from '~/interfaces/field.interface';
import { Gateway } from '~/models/gateways/gateway.model';
import apiService from '~/services/api.service';
import downloadService from '~/services/download.service';
import { LoadingModule } from '~common-store/modules/loading';
import { PopupModule } from '~common-store/modules/popup';

export class Camera extends Gateway {

    static MAX_TIME_BEFORE_CAMERA_UNKNOWN: number = 24 * 3600;
    hasOperationImportData = false;

    // TODO: use generated constants
    NtmoAPICqccInstall = 'START_INSTALL';
    NtmoAPICqccReset = 'RESET_HOMEKIT';

    battery_percent?: number;

    minimumRebootFirmware = Infinity;
    minimumUnlockFirmware = Infinity;
    minimumHomekitResetFirmware = Infinity;

    sd_status?: number;
    alim_status?: number;
    firmware_name?: string;

    thresholds = false;

    get outdatedLastStatusStored() {
        return Date.now() / 1000 - (this.last_connection_status || 0) > this.MAX_TIME_BEFORE_UNKNOWN;
    }

    get deviceOperations() {
        return super.deviceOperations.concat([
            operations.enableSsh({ callback: this.enableSsh, disabled: this.isEnableSshDisabled }),
            operations.fullReset({ callback: this.fullReset, disabled: this.isFullResetDisabled }),
            operations.orderReboot({ callback: this.orderReboot, disabled: this.isOrderRebootDisabled }),
            operations.ping({ callback: this.ping, disabled: this.isPingDisabled }),
            operations.statsDump({ callback: this.statsDump, disabled: this.isStatsDumpDisabled }),
            operations.statsDumpVerbose({ callback: this.statsDumpVerbose, disabled: this.isStatsDumpVerboseDisabled }),
            operations.timeline({ callback: this.timeline }),
            operations.unlock({ callback: this.unlock, disabled: this.isUnlockDisabled }),
        ]);
    }

    get isVpnDisconnected() {
        let value = false;
        if (this.network) {
            value = !this.network.vpn || typeof this.network.vpn.last_disconnection !== 'undefined';
        }
        return { value, reason: 'VPN disconnected' };
    }

    get canRebootCamera() {
        return {
            value: Boolean(this.firmware && this.firmware >= this.minimumRebootFirmware),
            reason: `Firmware < ${this.minimumRebootFirmware}`,
        };
    }

    get canUnlockCamera() {
        return {
            value: Boolean(this.firmware && this.firmware >= this.minimumUnlockFirmware),
            reason: `Firmware < ${this.minimumUnlockFirmware}`,
        };
    }

    get canHomekitResetCamera() {
        return {
            value: Boolean(this.firmware && this.firmware >= this.minimumHomekitResetFirmware),
            reason: `Firmware < ${this.minimumHomekitResetFirmware}`,
        };
    }

    get isNetworkLocked() {
        return {
            value: Boolean(this.network && this.network.network_lock),
            reason: 'Network locked',
        };
    }

    get isStatsDumpDisabled() {
        return this.isVpnDisconnected;
    }

    get isStatsDumpVerboseDisabled() {
        return this.isVpnDisconnected;
    }

    get isPingDisabled() {
        return this.isVpnDisconnected;
    }

    get isMainloopDisabled() {
        return {
            value: !this.canUnlockCamera.value,
            reason: this.canUnlockCamera.reason,
        };
    }

    get isFullResetDisabled() {
        if (this.isVpnDisconnected.value === true) {
            return this.isVpnDisconnected;
        } else if (this.isNetworkLocked.value === true) {
            return this.isNetworkLocked;
        } else {
            return { value: false };
        }
    }

    get isOrderRebootDisabled() {
        return {
            value: !this.canRebootCamera.value,
            reason: this.canRebootCamera.reason,
        };
    }

    get isUnlockDisabled() {
        if (this.isVpnDisconnected.value === true) {
            return this.isVpnDisconnected;
        } else if (this.canUnlockCamera.value === false) {
            return { value: true, reason: this.canUnlockCamera.reason };
        } else if (this.isNetworkLocked.value === false) {
            return { value: true, reason: 'Not network locked' };
        } else {
            return { value: false };
        }
    }

    get isHomekitResetDisabled() {
        return {
            value: !this.canHomekitResetCamera.value,
            reason: this.canHomekitResetCamera.reason,
        };
    }

    get isEnableSshDisabled() {
        return this.isVpnDisconnected;
    }

    get associateUserDisabled() {
        return {
            value: Boolean(this.homeId),
            reason: 'Device already associated',
        };
    }

    get fieldFirmware() {
        return firmwareCameras(this.firmware_name || this.firmware);
    }
    get fieldWifiChip() {
        const shieldConf = this.getWifiChipAndShieldConfig();
        if (shieldConf !== null) {
            return wifiChip(shieldConf.chip);
        }
    }
    get fieldShield() {
        const shieldConf = this.getWifiChipAndShieldConfig();
        if (shieldConf !== null) {
            return shield(shieldConf.shield);
        }
    }
    get fieldSdStatus() {
        return sdStatus(this.sd_status, this.sd_status === 4 ? 'success--text' : 'error--text');
    }
    get fieldMonitoring() {
        return monitoring(this.status, this.status === 'on' ? 'success--text' : 'error--text');
    }
    get fieldAlimStatus() {
        return alimStatus(this.alim_status, this.alim_status === 2 ? 'success--text' : 'error--text');
    }

    getWifiChipAndShieldConfig(): null | { chip: string, shield: boolean } {
        return null;
    }

    getNetworkFields(): FieldObject[] {
        let fields: FieldObject[] = [];
        if (this.network) {
            fields = [
                ip(this.network.ip),
                connection(this.getWifiIcon(this.network.connection), this.network.connection),
            ];
            if (this.canUnlockCamera.value) {
                fields.push(networkLock(
                    this.network.network_lock || false,
                    !this.network.network_lock ? 'success--text' : 'error--text',
                ));
            }
            if (!this.network.vpn) {
                return fields;
            }
            // Don't display oudated infos
            if (this.network.vpn.last_disconnection) {
                fields.push(lastDisconnection(this.network.vpn.last_disconnection, 'error--text'));
                return fields;
            }
            const outdatedValue = Date.now() / 1000 - this.network.vpn.connection_time
                > Camera.MAX_TIME_BEFORE_CAMERA_UNKNOWN;

            if (outdatedValue) {
                fields.push(outdated('The connection of the camera is outdated', 'error--text'));
            }

            fields.push(vpnServer(this.network.vpn.nas_id));
            fields.push(publicIp(this.network.vpn.public_ip || this.network.ip));
            fields.push(vpnIp(this.network.vpn.virtual_ip));
            fields.push(connectionTime(this.network.vpn.connection_time, outdatedValue ? 'error--text' : ''));
        }
        return fields;
    }

    electricalReset = async () => {
        await this.handleOperation({
            callback: () => {
                return this.sendVpnCommand({
                    command: 'electrical_reset',
                    textSuccess: 'Electrical Reset has been completed',
                });
            },
            confirmText: 'You are about to do an electrical reset on this camera, do you want to continue ?',
        });
    };

    homekitReset = async () => {
        await this.handleOperation({
            callback: () => {
                return this.sendVpnCommand({
                    command: 'homekit_reset',
                    textSuccess: 'Homekit Reset has been completed',
                });
            },
            confirmText: 'You are about to do a Homekit reset on this camera, do you want to continue ?',
        });
    };

    enableSsh = async () => {
        const resConfirm = confirm('***WARNING***\n/!\\\nPlease click OK only if the “SSH access authorization” box is ticked in the user\'s ticket!\n/!\\');
        if (resConfirm) {
            LoadingModule.showLoading(true);
            await apiService.adminenablessh({ device_id: this.id });
            LoadingModule.showLoading(false);
        }
    };

    fullReset = async () => {
        await this.handleOperation({
            callback: () => {
                return this.sendVpnCommand({ command: 'reset_factory', textSuccess: 'Full Reset has been completed' });
            },
            confirmText: 'You are about to full reset this camera, do you want to continue ?',
        });
    };

    ping = async () => {
        await this.handleOperation({
            callback: () => this.sendVpnCommand({ command: 'ping', textSuccess: 'Ping' }),
        });
    };

    installQRCode = async () => {
        await this.handleOperation({
            callback: () => this.generateQrCode({ cmd: this.NtmoAPICqccInstall, type: 'setup' }),
        });
    };

    resetQRCode = async () => {
        await this.handleOperation({
            callback: () => this.generateQrCode({ cmd: this.NtmoAPICqccReset, type: 'reset' }),
        });
    };

    unlock = async () => {
        await this.handleOperation({
            callback: () => apiService.unlocknetwork({ device_id: this.id }),
        });
    };

    statsDump = async () => {
        return await this.statsDumpFunction({ verbose: false });
    };

    statsDumpVerbose = async () => {
        return await this.statsDumpFunction({ verbose: true });
    };

    statsDumpFunction = async ({ verbose = false }: { verbose: boolean }) => {
        try {
            LoadingModule.showLoading(true);
            const statsDumpValue = (
                await this.sendVpnCommand({ command: 'dump_stats', verbose }) as any
            ).data.body as string;
            return {
                dump: statsDumpValue,
                verbose,
            };
        } catch (error) {
            return {
                dump: 'Error while fetching stats dump',
                verbose,
            };
        } finally {
            PopupModule.setLastExecuted(new Date());
            LoadingModule.showLoading(false);
        }
    };

    sendVpnCommand = async (
        { command, verbose, textSuccess }:
        { command: string, verbose?: boolean, textSuccess?: string },
    ) => {
        const params: { camera_id: string, command: string, verbose?: boolean } = { camera_id: this.id, command };
        if (verbose) {
            params.verbose = true;
        }
        return await apiService.adminsendvpncommand(params, textSuccess, 'The camera vpn command ' + command + ' has failed');
    };

    timeline = async () => {
        try {
            LoadingModule.showLoading(true);
            return (await apiService.admingetcameraevents({ camera_id: this.id })).data.body.events;
        } catch (error) {
            return [];
        } finally {
            PopupModule.setLastExecuted(new Date());
            LoadingModule.showLoading(false);
        }
    };

    generateQrCode = async ({ cmd, type }: { cmd: string, type: string }) => {
        const params = {
            camera_id: this.id,
            cmd,
        };
        const textSuccess = `Operation ${type} successfully sent`;
        const res = await apiService.admingenerateqrcode(params, textSuccess) as any;
        const options = {
            filename: `qrCode-${this.id}-${type}.png`,
            data: res.data,
        };
        downloadService.download(options);
    };
}
